/**
 * Valor estimado respondido no formulario — a regua pt-BR (ADR-0059, FORM-03).
 *
 * O servidor lia o valor com `Decimal(str(valor))`, leitura en-US: "80.000"
 * virava R$ 80,00 e entrava assim na soma do pipeline, "R$ 80.000,00" era
 * descartado sem aviso e um telefone digitado no campo estourava o
 * numeric(12,2) com 500 e rollback do lead. Agora o servidor le pt-BR e
 * devolve erro POR CAMPO; esta e a mesma regua, aplicada antes do envio.
 *
 * Texto com letra alem de "R$" e ILEGIVEL, nao numero: `parseDinheiro`
 * descarta letras e cola os digitos ("entre 50 e 80 mil" -> 5080), o que
 * serve ao importador de planilha (a coluna ja e de dinheiro) mas aqui
 * inventaria um valor a partir da frase do visitante.
 *
 * Mesma tabela de casos de `formularios.services._valor_estimado_ou_recusa`.
 */
import {
  DESTINO_VALOR_ESTIMADO,
  TIPOS_COM_VALOR_ESTIMADO,
  nomeExternoDoCampo,
} from "../../services/formularios";
import { formatarDinheiro, parseDinheiro } from "../../utils/dinheiro";

import { erroDoIntervaloNumerico } from "./limitesCampo";

/** Teto de `Lead.valor_estimado` — a coluna e numeric(12,2). */
export const VALOR_ESTIMADO_MAXIMO = 9999999999.99;

// Os textos do servidor, sem o rotulo do campo na frente.
export const MSG_VALOR_ESTIMADO = "informe o valor em reais, ex.: 80.000";
export const MSG_VALOR_ESTIMADO_TETO =
  "valor alto demais — o máximo é R$ 9.999.999.999,99";

export const AJUDA_VALOR_ESTIMADO = "Em reais, ex.: 80.000.";

const LETRA = /\p{L}/u;
const SIMBOLO_DE_REAL = /r\$/gi;

/** O campo grava dinheiro no lead? (o servidor so aceita texto e numero). */
export function campoDeValorEstimado(campo) {
  return (
    nomeExternoDoCampo(campo) === DESTINO_VALOR_ESTIMADO
    && TIPOS_COM_VALOR_ESTIMADO.includes(campo?.tipo)
  );
}

/** Tem letra alem de "R$"? Entao e frase, nao dinheiro. */
export function textoDeDinheiroIlegivel(valor) {
  return LETRA.test(String(valor ?? "").replace(SIMBOLO_DE_REAL, ""));
}

/** `{numero}` quando da para ler, `{erro}` com o texto do servidor quando nao. */
export function lerValorEstimado(valor) {
  const bruto = Array.isArray(valor) ? valor[0] : valor;
  if (textoDeDinheiroIlegivel(bruto)) return { erro: MSG_VALOR_ESTIMADO };
  const numero = parseDinheiro(bruto);
  if (numero === null) return { erro: MSG_VALOR_ESTIMADO };
  if (Math.abs(numero) > VALOR_ESTIMADO_MAXIMO) return { erro: MSG_VALOR_ESTIMADO_TETO };
  return { numero };
}

/** Erro do valor PREENCHIDO, com o rotulo na frente; "" quando passa. */
export function erroDoValorEstimado(campo, valor) {
  const label = campo?.label || campo?.tipo;
  const { numero, erro } = lerValorEstimado(valor);
  if (erro) return `${label}: ${erro}.`;
  return erroDoIntervaloNumerico(campo, numero);
}

/**
 * Texto do campo depois do blur: "80000" vira "80.000,00", para o visitante
 * conferir o que esta mandando. O que nao da para ler volta como veio — quem
 * aponta o problema e a validacao, no rotulo do campo.
 */
export function valorEstimadoNoBlur(texto) {
  const { numero } = lerValorEstimado(texto);
  return numero === undefined ? String(texto ?? "") : formatarDinheiro(numero);
}
